"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, ChevronDownIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";

// // // //

interface Props {
    value: string;
    onChange: (updatedValue: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

// // // //

// Bounds for the month/year dropdowns. Wide enough to cover most use cases
// (e.g. far-future maturity dates) without navigation getting unbounded.
const CURRENT_YEAR = new Date().getFullYear();
const CALENDAR_START_MONTH = new Date(CURRENT_YEAR - 100, 0, 1);
const CALENDAR_END_MONTH = new Date(CURRENT_YEAR + 50, 11, 31);

/**
 * A date picker component using shadcn/ui's Calendar component.
 * Provides a popover with a calendar for selecting dates.
 */
export function DateInput(props: Props) {
    const { value, onChange, placeholder = "Pick a date", disabled } = props;

    const selectedDate = value ? new Date(value) : undefined;

    const handleSelect = (date: Date | undefined) => {
        if (date) {
            onChange(date.toISOString());
        }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                        "w-full justify-between text-left font-normal",
                        !value && "text-muted-foreground",
                    )}
                >
                    <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4" />
                        {value ? (
                            format(new Date(value), "MM/d/yyyy")
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </div>
                    <ChevronDownIcon className="h-4 w-4 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0 rounded-lg overflow-clip"
                align="start"
            >
                <Calendar
                    mode="single"
                    captionLayout="dropdown"
                    startMonth={CALENDAR_START_MONTH}
                    endMonth={CALENDAR_END_MONTH}
                    selected={selectedDate}
                    onSelect={handleSelect}
                    defaultMonth={selectedDate}
                    disabled={disabled}
                />
            </PopoverContent>
        </Popover>
    );
}
